import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface NavbarProps {
  onCtaClick: () => void;
}

export default function Navbar({ onCtaClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Services', href: '#system-overview' },
    { label: 'Industries', href: '#industries' },
    { label: 'How It Works', href: '#how-it-works' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 dark:bg-[#070D1B]/90 border-b border-black/8 dark:border-white/10'
          : 'bg-transparent'
      }`}
      style={{ backdropFilter: scrolled ? 'saturate(180%) blur(20px)' : 'none' }}
    >
      <nav className="px-8 lg:px-16 h-20 flex items-center justify-between">
        <a href="/" className="flex items-center cursor-pointer">
          <div className="flex items-center justify-center" style={{ width: 150, height: 150 }}>
            <img
              src="https://public.readdy.ai/ai/img_res/bf69462a-f180-485e-aa00-1081295b3e42.png"
              alt="Henexa AI"
              style={{ width: 150, height: 150, objectFit: 'contain' }}
            />
          </div>
        </a>

        <div className="hidden md:flex items-center gap-9">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={`text-sm tracking-wide transition-colors cursor-pointer ${
                scrolled
                  ? 'text-[#0B2A4A]/65 hover:text-[#0B2A4A] dark:text-white/60 dark:hover:text-white'
                  : 'text-white/75 hover:text-white'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="#contact"
            className={`text-sm tracking-wide transition-colors cursor-pointer ${
              scrolled
                ? 'text-[#0B2A4A]/65 hover:text-[#0B2A4A] dark:text-white/60 dark:hover:text-white'
                : 'text-white/75 hover:text-white'
            }`}
          >
            Contact
          </a>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              scrolled
                ? 'text-[#0B2A4A]/50 hover:text-[#0B2A4A] hover:bg-[#F8FAFC] dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10'
                : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              {theme === 'dark' ? (
                <i className="ri-sun-line text-base" />
              ) : (
                <i className="ri-moon-line text-base" />
              )}
            </div>
          </button>

          <button
            onClick={onCtaClick}
            className={`px-5 py-2 text-sm font-medium tracking-wide rounded-full transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'bg-[#0B2A4A] text-white hover:bg-[#1E3A8A] dark:bg-white dark:text-[#0B2A4A] dark:hover:bg-white/90'
                : 'bg-white/15 text-white border border-white/25 hover:bg-white/25'
            }`}
          >
            Build Your System
          </button>
        </div>

        <div className="md:hidden flex items-center gap-3">
          {/* Theme toggle mobile */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-8 h-8 flex items-center justify-center cursor-pointer rounded-full ${
              scrolled ? 'text-[#0B2A4A]/60 dark:text-white/60' : 'text-white/70'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              {theme === 'dark' ? (
                <i className="ri-sun-line text-base" />
              ) : (
                <i className="ri-moon-line text-base" />
              )}
            </div>
          </button>
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl ${scrolled ? 'text-[#0B2A4A] dark:text-white' : 'text-white'}`} />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div
          className="md:hidden bg-white/95 dark:bg-[#070D1B]/97 border-b border-black/8 dark:border-white/10 px-8 py-6 flex flex-col gap-5"
          style={{ backdropFilter: 'blur(20px)' }}
        >
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-sm text-[#0B2A4A]/65 dark:text-white/65 tracking-wide cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => { setMenuOpen(false); onCtaClick(); }}
            className="mt-2 px-5 py-2.5 bg-[#0B2A4A] dark:bg-white dark:text-[#0B2A4A] text-white text-sm font-medium tracking-wide rounded-full whitespace-nowrap cursor-pointer text-center"
          >
            Build Your System
          </button>
        </div>
      )}
    </header>
  );
}
